import {
  onMounted,
  onActivated,
  onBeforeUnmount,
  getCurrentInstance,
  watch,
  computed,
  ComputedRef,
  isRef,
  provide,
  inject,
} from 'vue';
import {
  isArray,
  filter,
  isString,
  isEmpty,
  get,
  set,
  isUndefined,
  isObject,
} from 'lodash-es';
import debug from 'debug';
import { useHelpers, IAppHelpers } from './useHelpers';
import { useComponentState, IComponentState } from './useComponentState';
import { useForms } from './useForms';
import { useDataLoader, DataLoaderMap } from './useDataLoader';
import {
  ComponentDataSpec,
  ComponentMetadata,
  ComponentUXSpec,
  FormInstance,
} from '../schemas';

const log = debug('itp:core:useUserComponent');

export interface UserComponentOptions {
  metadata: ComponentMetadata;
  dataSpecs: ComponentDataSpec[];
  loaders: DataLoaderMap;
  ux: ComponentUXSpec;
  dataRefs?: Record<string, any>;
}

export interface SetDataOptions {
  noTouchLastLoaded?: boolean;
  noTakeSnapshot?: boolean;
}

export interface IUserComponent extends IAppHelpers, Omit<IComponentState, 'friendlyName'> {
  forms: Record<string, FormInstance | null>;
  registerForm: (name: string, form: FormInstance) => void;
  unregisterForm: (form: FormInstance) => void;
  loadData: (prop?: string | null, opts?: { initial?: boolean }) => Promise<void>;
  loadDataThrottled: (prop?: string | null, opts?: { initial?: boolean }) => Promise<void>;
  touchLastLoaded: () => void;
  handleError: (error: any) => void;
  friendlyName: () => string;
  documentTitle: ComputedRef<string | undefined>;
  updateHeaders: () => void;
  setData: (path: string, value: any, opts?: SetDataOptions) => void;
}

export function useUserComponent(props: any, options: UserComponentOptions): IUserComponent {
  const instance = getCurrentInstance();
  const context = instance?.proxy;

  const helpers = useHelpers();
  const state = useComponentState(props);

  state.componentMetadata.value = options.metadata;
  state.ux.value = options.ux;

  if (instance) {
    state.$uid.value = instance.type.name || 'Anonymous';
  }

  if (props.modal) {
    state.isModalComponent.value = true;
  }
  if (props.modalName) {
    state.modalName.value = props.modalName;
  }

  const inheritedInsideModal = inject<boolean>('itp-inside-modal', false);
  const isInsideModal = !!props.modal || inheritedInsideModal;
  provide('itp-inside-modal', isInsideModal);

  const friendlyName = () => state.modalName.value || state.$uid.value;

  const { forms, registerForm: localRegisterForm, unregisterForm: localUnregisterForm } = useForms(friendlyName);

  const isRoot = ['page', 'modal'].includes(options.metadata.kind);

  if (isRoot) {
    provide('itp-root-register-form', localRegisterForm);
    provide('itp-root-unregister-form', localUnregisterForm);
  }

  const rootRegisterForm = isRoot
    ? null
    : inject<((name: string, form: FormInstance) => void) | null>('itp-root-register-form', null);
  const rootUnregisterForm = isRoot
    ? null
    : inject<((form: FormInstance) => void) | null>('itp-root-unregister-form', null);

  const registerForm = (name: string, form: FormInstance) => {
    localRegisterForm(name, form);
    if (!isRoot && rootRegisterForm) {
      rootRegisterForm(name, form);
    }
  };

  const unregisterForm = (form: FormInstance) => {
    localUnregisterForm(form);
    if (!isRoot && rootUnregisterForm) {
      rootUnregisterForm(form);
    }
  };

  provide('itp-register-form', registerForm);
  provide('itp-unregister-form', unregisterForm);

  const dataLoaderState = {
    ...state,
    ...(options.dataRefs || {}),
  };

  const dataLoader = useDataLoader({
    state: dataLoaderState,
    helpers,
    friendlyName,
    dataSpecs: options.dataSpecs,
    loaders: options.loaders,
    context,
    ux: options.ux,
  });

  const setData = (path: string, value: any, opts?: SetDataOptions): void => {
    const pathParts = path.split('.');
    const rootName = pathParts[0];
    const dataRef = options.dataRefs?.[rootName];

    if (dataRef && isRef(dataRef)) {
      if (pathParts.length === 1) {
        dataRef.value = value;
      } else {
        const nestedPath = pathParts.slice(1).join('.');
        const refValue = dataRef.value;
        if (isUndefined(get(refValue, nestedPath))) {
          console.warn(
            `setData: ${path} path is undefined on component ${friendlyName()}. ` +
              'This might be a problem if this value is expected to be reactive.',
          );
        }
        if (isObject(refValue)) {
          set(refValue as object, nestedPath, value);
        }
      }
    } else {
      const stateRef = (state as any)[rootName];
      if (stateRef && isRef(stateRef)) {
        if (pathParts.length === 1) {
          stateRef.value = value;
        } else {
          const nestedPath = pathParts.slice(1).join('.');
          const refValue = stateRef.value;
          if (isObject(refValue)) {
            set(refValue as object, nestedPath, value);
          }
        }
      } else if (pathParts.length > 1 && rootName in props && isObject(props[rootName])) {
        const nestedPath = pathParts.slice(1).join('.');
        set(props[rootName] as object, nestedPath, value);
      } else {
        console.warn(
          `setData: ${path} could not be resolved on component ${friendlyName()}.`,
        );
      }
    }

    log('[%s] setData %s := %o', friendlyName(), path, value);

    if (!opts || !opts.noTouchLastLoaded) {
      dataLoader.touchLastLoaded();
    }
  };

  const documentTitle = computed(() => {
    if (state.pageDocument.value) {
      const result = state.pageDocument.value.title;
      if (isArray(result)) {
        return filter(result, (p) => isString(p) && !isEmpty(p)).join(' | ');
      }
      return result as string;
    }
    return undefined;
  });

  const ROUTE_TAB_LOADING_PLACEHOLDER = '..';

  const setRouteTabTitle = (title: string) => {
    log("updateRouteTab [%s] to '%s'", friendlyName(), title);

    if (state.routeTab.value) {
      state.routeTab.value.title = title;
      state.routeTab.value.tips = title;
      state.routeTab.value.icon = state.pageDocument.value?.icon;
    } else {
      state.routeTab.value = {
        title,
        tips: title,
        icon: state.pageDocument.value?.icon,
      };
    }
  };

  const updateRouteTab = () => {
    if (isInsideModal) {
      return;
    }
    const title = documentTitle.value;

    if (title) {
      setRouteTabTitle(title);
      window.dispatchEvent(
        new CustomEvent('itp:routeTab', {
          detail: {
            title,
            icon: state.pageDocument.value?.icon,
          },
        }),
      );
    }
  };

  const documentTitleOwner = Symbol('itp-document-title-owner');

  const updateHeaders = () => {
    if (isInsideModal) {
      return;
    }

    if (state.loaded.value && documentTitle.value) {
      helpers.$app.value.documentTitle.set(
        documentTitle.value,
        documentTitleOwner,
      );
      updateRouteTab();
    } else if (!state.loaded.value) {
      setRouteTabTitle(ROUTE_TAB_LOADING_PLACEHOLDER);
    }
  };

  watch(state.loaded, () => {
    updateHeaders();
  });

  watch(
    state.pageDocument,
    () => {
      updateHeaders();
    },
    { deep: true },
  );

  // "created() { ... this.loadData(...) }"
  {
    helpers.$app.value
      .waitForReady()
      .then(() => {
        return dataLoader.loadData(null, { initial: true });
      })
      .catch((error) => {
        log('[%s] error loading data:', friendlyName(), error);
        const useGlobalModal =
          options.ux && options.ux.errorHandling === 'global';
        if (useGlobalModal) {
          if (props.componentInitErrorCallback) {
            props.componentInitErrorCallback(error);
          } else if (instance?.attrs?.componentInitErrorCallback) {
            (instance.attrs.componentInitErrorCallback as Function)(error);
          }
        }
      });
  }

  onMounted(() => {
    log('[%s] Component mounted', friendlyName());
    updateHeaders();
  });

  onActivated(() => {
    log('[%s] Component activated', friendlyName());
    updateHeaders();
  });

  onBeforeUnmount(() => {
    if (state.isModalComponent.value || state.routeTab.value) {
      helpers.$app.value.documentTitle.reset(documentTitleOwner);
    }
    // unsubscribeFromGlobalEvents(); // TODO: unsub
  });

  watch(
    () => props.reload,
    () => {
      if (state.canLoadData.value) {
        dataLoader.loadData();
      }
    },
  );

  return {
    ...helpers,
    ...state,
    forms,
    registerForm,
    unregisterForm,
    ...dataLoader,
    friendlyName,
    documentTitle,
    updateHeaders,
    setData,
  };
}
